import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import { uspContent } from './constants'
import { Trans, useTranslation } from 'react-i18next'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Pane from 'components/Common/Pane'
import { rem } from 'polished'
import { setBubbleAnimation, setValueAnimation } from './utils'
import TextButton from 'components/Luxkit/Button/TextButton'
import { accountAccessShowLogin } from 'actions/UiActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import * as Analytics from 'analytics/analytics'
import { uspAboutUsEvent } from 'analytics/snowplow/events'
import { isLoggedIn } from 'selectors/accountSelectors'
import { connect } from 'react-redux'
import { useIsMobileAndSmallScreen } from 'lib/deviceUtils'

const Container = styled.div`
  background-color: ${({ theme }) => theme.palette.neutral.eight};
`
const StyledLayoutContainer = styled(LayoutContainer)`
  border-top: 1px solid ${({ theme }) => theme.palette.neutral.five};
  padding-left: ${rem(16)};
  padding-right: 0;
  ${mediaQueryUp.md}{
    padding-right: ${rem(16)};
  }
`

const ContainerRight = styled.div`
  padding-right: ${rem(16)};
  ${mediaQueryUp.md}{
    padding-right: 0;
  }
`

const StyledPane = styled(Pane)`
  overflow: hidden;
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`
const Values = styled.div`
  ${setValueAnimation}
`

const CarouselBubbles = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: ${rem(16)};
  gap: ${rem(4)};

  ${mediaQueryUp.md} {
    justify-content: center;
  }
`

const CarouselBubble = styled.div`
  ${setBubbleAnimation}
`

const UspBlockGroup = styled(Group)`
    padding-right: ${rem(24)};
    width: 100%;
`

const USPGroup = styled(Group)`
  padding-top: ${rem(24)};
`

const CTAGroup = styled(Group)`
  padding-top: ${rem(24)};
`

interface MappedProps {
  loggedIn: boolean | undefined;
  regionCode: string;
}

function FrontPageUSPComponent(props: MappedProps) {
  const { loggedIn, regionCode } = props
  const { t } = useTranslation('uspComponent')
  const values = uspContent(t, regionCode)
  const dispatch = useAppDispatch()
  const isTabletOrMobile = useIsMobileAndSmallScreen()

  const onLearnMoreClick = useCallback(() => {
    Analytics.track('usp-about-us-event', uspAboutUsEvent('learn_about_us'))
  }, [])

  const onSignupClick = useCallback(() => {
    dispatch(accountAccessShowLogin())
  }, [dispatch])

  return <Container>
    <StyledLayoutContainer>
      <StyledPane>
        <ContainerRight>
          <Heading variant="heading2">
            { loggedIn && <Trans i18nKey="titleLoggedIn" ns="uspComponent">
              Discover a <i>better way</i> to travel
            </Trans>}
            { !loggedIn && <Trans i18nKey="titleGuest" ns="uspComponent">
              Join today and discover a <i>better way</i> to travel
            </Trans>}
          </Heading>
        </ContainerRight>
        <USPGroup direction="vertical" gap={16}>
          <Values>
            {values.map(({ Icon, heading, body }) => (
              <UspBlockGroup key={`${heading}-regular`} direction="vertical">
                <Group direction="vertical" gap={4} >
                  <Group direction="horizontal" gap={4} verticalAlign="start">
                    {Icon}
                    <BodyText variant="medium" weight="bold">
                      {heading}
                    </BodyText>
                  </Group>
                  <BodyText variant="medium" colour="neutral-three">
                    {body}
                  </BodyText>
                </Group>
              </UspBlockGroup>
            ))}
            {/* This is needed to give the impression of an infinitie loop */}
            {isTabletOrMobile && values.map(({ Icon, heading, body }) => (
              <UspBlockGroup key={`${heading}-infinite`} direction="vertical">
                <Group direction="vertical" gap={4} >
                  <Group direction="horizontal" gap={4} verticalAlign="start">
                    {Icon}
                    <BodyText variant="medium" weight="bold">
                      {heading}
                    </BodyText>
                  </Group>
                  <BodyText variant="medium" colour="neutral-three">
                    {body}
                  </BodyText>
                </Group>
              </UspBlockGroup>
            ))}
          </Values>
        </USPGroup>
        <ContainerRight>
          <CSSBreakpoint max="sm">
            <CarouselBubbles>
              {Array.from({ length: values.length }).map((_, index) => (
                <CarouselBubble key={values[index].heading + '-bubbles'} />
              ))}
            </CarouselBubbles>
          </CSSBreakpoint>
          <CSSBreakpoint min="sm">
            <CTAGroup gap={8} direction="horizontal" smHorizontalAlign="end">
              <TextButton kind="tertiary" onClick={onLearnMoreClick} to="/about-us" target="_blank">
                {t('Learn more about us', { ns: 'uspComponent' })}
              </TextButton>
              { !loggedIn && <TextButton kind="primary" onClick={onSignupClick}>
                {t('Sign up and save', { ns: 'uspComponent' })}
              </TextButton>
            }
            </CTAGroup>
          </CSSBreakpoint>
          <CSSBreakpoint max="xs">
            <CTAGroup gap={8} direction="vertical">
              { !loggedIn && <TextButton kind="primary" onClick={onSignupClick}>
                {t('Sign up and save', { ns: 'uspComponent' })}
              </TextButton>
          }
              <TextButton kind="tertiary" onClick={onLearnMoreClick} to="/about-us" target="_blank">
                {t('Learn more about us', { ns: 'uspComponent' })}
              </TextButton>
            </CTAGroup>
          </CSSBreakpoint>
        </ContainerRight>
      </StyledPane>
    </StyledLayoutContainer>
  </Container>
}

function mapStateToProps(state: App.State) {
  return {
    loggedIn: isLoggedIn(state),
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(FrontPageUSPComponent)
